import { Fragment } from "react";
import { Alert, Breadcrumbs, Link, Typography } from "@mui/material";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { AwsIntegrationForm } from "./forms/aws/AwsIntegrationForm";
import { OktaIntegrationForm } from "./forms/OktaIntegrationForm";
import { AzureIntegrationForm } from "./forms/azure/AzureIntegrationForm";
import { AzureBlobIntegrationForm } from "./forms/AzureBlobIntegrationForm";
import { BigQueryIntegrationForm } from "./forms/BigQueryIntegrationForm";
import { PostgreSQLIntegrationForm } from "./forms/PostgreSQLIntegrationForm";
import { SnowflakeIntegrationForm } from "./forms/SnowflakeIntegrationForm";
import { S3IntegrationForm } from "./forms/S3IntegrationForm";
import { S3CompatibleIntegrationForm } from "./forms/S3CompatibleIntegrationForm";
import { GcsBucketIntegrationForm } from "./forms/GcsBucketIntegrationForm";
import { GcpIntegrationForm } from "./forms/GcpIntegrationForm";
import { Ms365IntegrationForm } from "./forms/ms365/Ms365IntegrationForm";
import { SlackIntegrationForm } from "./forms/SlackIntegrationForm";
import { GitlabIntegrationForm } from "./forms/GitlabIntegrationForm";
import { GithubIntegrationForm } from "./forms/GithubIntegrationForm";
import { GoogleWorkspaceIntegrationForm } from "./forms/GoogleWorkspaceIntegrationForm";
import { mapIntegrationRouteToTitle } from "./utils";
import { Home as HomeIcon } from "@mui/icons-material";
import { useGetClientIntegrationQuery } from "~/operations";
import { JiraIntegrationForm } from "./forms/jira/JiraIntegrationForm/JiraIntegrationForm";
import { OciIntegrationForm } from "./forms/OciIntegrationForm";
import { DomainIntegrationForm } from "./forms/DomainIntegrationForm";
import { EmailIntegrationForm } from "~/pages/integrations/hosted-integrations/forms/email/EmailIntegrationForm";
import { ZendeskIntegrationForm } from "./forms/zendesk/ZendeskIntegrationForm";
import { MsftDefenderIntegrationForm } from "./forms/MsftDefenderIntegrationForm";
import { GithubIssuesIntegrationForm } from "./forms/github-issues/GithubIssuesIntegrationForm";
import { GitlabIssuesIntegrationForm } from "./forms/gitlab-issues/GitlabIssuesIntegrationForm";
import { AzureDevOpsIntegrationForm } from "~/pages/integrations/hosted-integrations/forms/azure-devops/AzureDevOpsIntegrationForm";
import { ShodanIntegrationForm } from "./forms/ShodanIntegrationForm";
import { SentinelOneIntegrationForm } from "./forms/SentinelOneIntegrationForm";
import { ServiceNowIntegrationForm } from "~/pages/integrations/hosted-integrations/forms/servicenow/ServiceNowIntegrationForm/ServiceNowIntegrationForm";
import { CrowdStrikeIntegrationForm } from "./forms/CrowdStrikeIntegrationForm";
import { setDocumentTitle } from "~/lib/commonUtils";
import { INTEGRATION_MRN } from "~/pages/integrations/utils/integrationConstants";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";
import {
  getIntegrationMrn,
  getIntegrationsListHref,
} from "~/pages/integrations/utils/integrationLinksUtils";
import { WebhookIntegrationForm } from "./forms/webhook/WebhookIntegrationForm";

export interface HostedIntegrationEditPageProps {
  scope: SpaceScope | OrganizationScope;
}

export function HostedIntegrationEditPage({
  scope,
}: HostedIntegrationEditPageProps) {
  const { integrationId, integrationTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const integrationMrnParam = searchParams.get(INTEGRATION_MRN);
  const mrn =
    integrationMrnParam || getIntegrationMrn({ scope, integrationId });
  const { data, error, loading } = useGetClientIntegrationQuery({
    variables: { mrn },
  });
  const integrationTitle = mapIntegrationRouteToTitle(integrationTypeId);

  const { integration } = data?.clientIntegration || {};

  setDocumentTitle([
    "Edit",
    integration?.name,
    integrationTitle,
    "Integrations",
    scope.name,
  ]);

  if (error) {
    return <LoadingFailedPage what={`${integrationTitle} integration`} />;
  }

  if (loading || !integration) {
    return <LoadingPage what={`${integrationTitle} integration`} />;
  }

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={getIntegrationsListHref({ scope })}
    >
      Integrations
    </Link>,
    <Link
      key={`/space/integrations/${integrationTypeId}`}
      component={RouterLink}
      to={getIntegrationsListHref({ scope, integrationTypeId })}
    >
      {integrationTitle}
    </Link>,
    <Link
      key={`/space/integrations/${integrationTypeId}/:integrationId`}
      component={RouterLink}
      to={
        scope.type === ScopeType.Space
          ? `/space/integrations/${integrationTypeId}/${integrationId}?${scope.params}&integrationMrn=${integration.mrn}`
          : `/organization/integrations/${integrationTypeId}/${integrationId}?${scope.params}&integrationMrn=${integration.mrn}`
      }
    >
      {integration.name}
    </Link>,
    <Typography
      key={`/space/integrations/${integrationTypeId}/integrationId/edit`}
      color="text.primary"
    >
      Edit
    </Typography>,
  ];

  const getIntegrationForm = (integrationTypeId: string | undefined) => {
    if (!integrationTypeId) return <NoIntegrationsToEdit {...{ scope }} />;

    switch (integrationTypeId.toLowerCase()) {
      case "aws":
      case "aws_hosted":
        return (
          <AwsIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "azure":
        return (
          <AzureIntegrationForm
            {...{
              scope,
              updateFlow: { integration },
            }}
          />
        );
      case "azure_blob":
        return (
          <AzureBlobIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "bigquery":
        return (
          <BigQueryIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "postgres":
        return (
          <PostgreSQLIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "shodan":
        return (
          <ShodanIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "snowflake":
        return (
          <SnowflakeIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "amazons3":
        return (
          <S3IntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "amazon-s3-compatible":
        return (
          <S3CompatibleIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "crowdstrike":
        return (
          <CrowdStrikeIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "defender":
        return (
          <MsftDefenderIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "sentinelone":
        return (
          <SentinelOneIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "host":
        return (
          <DomainIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "gcs_bucket":
        return (
          <GcsBucketIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "gcp":
        return (
          <GcpIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "ms365":
        return (
          <Ms365IntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "okta":
        return (
          <OktaIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "oci":
        return (
          <OciIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "hosted_slack":
        return (
          <SlackIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "gitlab":
        return (
          <GitlabIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "github":
        return (
          <GithubIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "google_workspace":
        return (
          <GoogleWorkspaceIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "ticket_system_jira":
        return (
          <JiraIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "ticket_system_email":
        return (
          <EmailIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "ticket_system_zendesk":
        return (
          <ZendeskIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );

      case "ticket_system_github":
        return (
          <GithubIssuesIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );

      case "ticket_system_gitlab":
        return (
          <GitlabIssuesIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "ticket_system_azure_devops":
        return (
          <AzureDevOpsIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );
      case "ticket_system_webhook":
        return (
          <WebhookIntegrationForm {...{ scope, updateFlow: { integration } }} />
        );
      case "ticket_system_servicenow":
        return (
          <ServiceNowIntegrationForm
            {...{ scope, updateFlow: { integration } }}
          />
        );

      default:
        return <NoIntegrationsToEdit {...{ scope }} />;
    }
  };

  setDocumentTitle(["Edit", integration.name, integrationTitle]);

  return (
    <Fragment>
      <Breadcrumbs sx={{ mb: 5 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {getIntegrationForm(integrationTypeId)}
    </Fragment>
  );
}

const NoIntegrationsToEdit = ({
  scope,
}: {
  scope: SpaceScope | OrganizationScope;
}) => {
  return (
    <Alert variant="outlined" severity="error">
      <Typography>
        Oops, something may have gone wrong or you have no integration to edit.
      </Typography>
      <Typography variant="caption">
        You may try reloading the page or navigating to view{" "}
        <Link component={RouterLink} to={`/space/integrations?${scope.params}`}>
          all of your integrations
        </Link>
        .
      </Typography>
    </Alert>
  );
};
